import { useEffect, useState } from 'react'
import type { AnalyticsFilters, FilterKey } from '@pharmacia/core'
import { get } from '../../lib/api.ts'
import { Combobox, type Option } from '../Combobox.tsx'
import { Select } from '../ui.tsx'
import { IconClose } from '../icons.tsx'

// The filter bar: one row, above the charts, never beside them.
//
// It stays put as the person moves between groups. A chart takes the keys
// it has a column for and ignores the rest -- the keys it ignores are
// dimmed here rather than hidden, so nobody is left wondering whether the
// doctor filter is being applied to the age histogram.

export type Picks = {
  doctor: Option | null
  patient: Option | null
  product: Option | null
}

const DAY = 86_400_000
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10)

/** Indian financial year: 1 April to 31 March. */
function financialYearStart(today: string): string {
  const d = new Date(today + 'T00:00:00Z')
  const y = d.getUTCMonth() >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1
  return `${y}-04-01`
}

export type Preset = 'today' | '7' | '30' | '90' | 'month' | 'fy' | 'all' | 'custom'

const PRESETS: { id: Preset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7', label: '7 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
  { id: 'month', label: 'This month' },
  { id: 'fy', label: 'Financial year' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' },
]

export function rangeFor(preset: Preset, today: string, earliest: string | null): { from: string; to: string } {
  const now = Date.parse(today + 'T00:00:00Z')
  switch (preset) {
    case 'today': return { from: today, to: today }
    case '7': return { from: iso(now - 6 * DAY), to: today }
    case '30': return { from: iso(now - 29 * DAY), to: today }
    case '90': return { from: iso(now - 89 * DAY), to: today }
    case 'month': return { from: today.slice(0, 8) + '01', to: today }
    case 'fy': return { from: financialYearStart(today), to: today }
    // "All time" starts at the first record the dispensary has, not at an
    // arbitrary early date: an axis that opens in 1970 is all whitespace.
    case 'all': return { from: earliest ?? iso(now - 364 * DAY), to: today }
    default: return { from: today, to: today }
  }
}

type Options = { districts: { value: string; count: number }[]; earliest: string | null; today: string }

export function FilterBar({ filters, picks, preset, onChange, honoured }: {
  filters: AnalyticsFilters
  picks: Picks
  preset: Preset
  onChange: (next: { filters: AnalyticsFilters; picks: Picks; preset: Preset }) => void
  /** The union of keys the charts on screen actually use. */
  honoured: Set<FilterKey>
}) {
  const [options, setOptions] = useState<Options | null>(null)

  useEffect(() => {
    let alive = true
    get<Options>('/api/analytics/options')
      .then(o => { if (alive) setOptions(o) })
      .catch(() => { /* the bar still works; only the district list is missing */ })
    return () => { alive = false }
  }, [])

  const set = (f: Partial<AnalyticsFilters>, p: Partial<Picks> = {}, pre = preset) =>
    onChange({ filters: { ...filters, ...f }, picks: { ...picks, ...p }, preset: pre })

  const choosePreset = (id: Preset) => {
    if (id === 'custom') return onChange({ filters, picks, preset: 'custom' })
    const r = rangeFor(id, options?.today ?? filters.to ?? '', options?.earliest ?? null)
    onChange({ filters: { ...filters, ...r }, picks, preset: id })
  }

  const dim = (k: FilterKey) => (honoured.has(k) ? 'f' : 'f off')
  const title = (k: FilterKey) =>
    honoured.has(k) ? undefined : 'Not used by the charts in this group'

  const chips: { key: FilterKey; label: string; value: string; clear: () => void }[] = []
  if (picks.doctor) chips.push({ key: 'doctorId', label: 'Doctor', value: picks.doctor.label, clear: () => set({ doctorId: undefined }, { doctor: null }) })
  if (picks.patient) chips.push({ key: 'patientId', label: 'Patient', value: picks.patient.label, clear: () => set({ patientId: undefined }, { patient: null }) })
  if (picks.product) chips.push({ key: 'productId', label: 'Product', value: picks.product.label, clear: () => set({ productId: undefined }, { product: null }) })
  if (filters.district) chips.push({ key: 'district', label: 'District', value: filters.district, clear: () => set({ district: undefined }) })
  if (filters.substance) chips.push({ key: 'substance', label: 'Substance', value: filters.substance, clear: () => set({ substance: undefined }) })
  if (filters.paymentMethod) chips.push({ key: 'paymentMethod', label: 'Paid by', value: filters.paymentMethod, clear: () => set({ paymentMethod: undefined }) })

  return (
    <div className="viz-filters">
      <div className="f grow" style={{ minWidth: 280 }}>
        <label>Period</label>
        <div className="viz-presets">
          {PRESETS.map(p => (
            <button
              key={p.id} type="button" aria-pressed={preset === p.id}
              onClick={() => choosePreset(p.id)}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {preset === 'custom' && (
        <>
          <div className="f" style={{ minWidth: 130 }}>
            <label htmlFor="vz-from">From</label>
            <input
              id="vz-from" className="input" type="date" value={filters.from ?? ''}
              max={filters.to} onChange={e => set({ from: e.target.value || undefined })}
            />
          </div>
          <div className="f" style={{ minWidth: 130 }}>
            <label htmlFor="vz-to">To</label>
            <input
              id="vz-to" className="input" type="date" value={filters.to ?? ''}
              min={filters.from} max={options?.today}
              onChange={e => set({ to: e.target.value || undefined })}
            />
          </div>
        </>
      )}

      <div className="f" style={{ minWidth: 120 }}>
        <label htmlFor="vz-bucket">Group by</label>
        <Select
          id="vz-bucket" value={filters.bucket}
          onChange={e => set({ bucket: e.target.value as AnalyticsFilters['bucket'] })}
        >
          <option value="auto">Automatic</option>
          <option value="day">Day</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
        </Select>
      </div>

      <div className={`${dim('doctorId')} wide`} title={title('doctorId')}>
        <label>Doctor</label>
        <Combobox
          endpoint="/api/lookup/doctors" value={picks.doctor} placeholder="Any doctor"
          onChange={o => set({ doctorId: o?.id }, { doctor: o })}
        />
      </div>

      <div className={`${dim('patientId')} wide`} title={title('patientId')}>
        <label>Patient</label>
        <Combobox
          endpoint="/api/lookup/patients" value={picks.patient} placeholder="Any patient"
          onChange={o => set({ patientId: o?.id }, { patient: o })}
        />
      </div>

      <div className={`${dim('productId')} wide`} title={title('productId')}>
        <label>Product</label>
        <Combobox
          endpoint="/api/lookup/products" value={picks.product} placeholder="Any product"
          onChange={o => set({ productId: o?.id }, { product: o })}
        />
      </div>

      <div className={dim('district')} title={title('district')}>
        <label htmlFor="vz-district">District</label>
        <Select
          id="vz-district" value={filters.district ?? ''}
          onChange={e => set({ district: e.target.value || undefined })}
        >
          <option value="">Any district</option>
          {options?.districts.map(d => (
            <option key={d.value} value={d.value}>{d.value} ({d.count})</option>
          ))}
        </Select>
      </div>

      <div className={dim('substance')} title={title('substance')}>
        <label htmlFor="vz-sub">Substance</label>
        <Select
          id="vz-sub" value={filters.substance ?? ''}
          onChange={e => set({ substance: (e.target.value || undefined) as AnalyticsFilters['substance'] })}
        >
          <option value="">Any</option>
          <option value="poppy">Poppy husk</option>
          <option value="opium">Opium</option>
          <option value="tola">Tola</option>
        </Select>
      </div>

      <div className={dim('paymentMethod')} title={title('paymentMethod')}>
        <label htmlFor="vz-pay">Paid by</label>
        <Select
          id="vz-pay" value={filters.paymentMethod ?? ''}
          onChange={e => set({ paymentMethod: (e.target.value || undefined) as AnalyticsFilters['paymentMethod'] })}
        >
          <option value="">Any method</option>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="card">Card</option>
          <option value="other">Other</option>
        </Select>
      </div>

      {chips.length > 0 && (
        <div className="viz-chips" style={{ flexBasis: '100%' }}>
          {chips.map(c => (
            <span className="viz-chip" key={c.key}>
              {c.label}: <b>{c.value}</b>
              <button type="button" onClick={c.clear} aria-label={`Clear ${c.label} filter`}>
                <IconClose size={12} />
              </button>
            </span>
          ))}
          <button
            type="button" className="viz-toggle"
            onClick={() => onChange({
              filters: { from: filters.from, to: filters.to, bucket: filters.bucket },
              picks: { doctor: null, patient: null, product: null },
              preset,
            })}
          >Clear all</button>
        </div>
      )}
    </div>
  )
}
